import { useCallback, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { adminAccessKeys, getMyPermissions } from "@/api/admin-access";
import { useAuth } from "@/hooks/use-auth";
import type { PermissionKey } from "@/lib/permissions";

/**
 * What the signed-in admin may do, from `GET /admin-access/me`.
 *
 * The one source the panel asks — the sidebar, the route guard and every
 * action button — so they cannot disagree with each other. The server checks
 * every request regardless; this decides only what is shown.
 *
 * Refetched every minute and on returning to the tab, so a permission an owner
 * changes reaches an open session without a sign-out. The server applies it
 * within 30 s, so the panel lags it by at most a minute and a half — and in
 * that window the worst case is a button that answers 403.
 *
 * Only the app shell passes `poll`: an interval runs per mounted observer and
 * ignores `staleTime`, so every caller polling would multiply the requests.
 *
 * While loading, `can` says no: a link that appears a moment late is better
 * than one that appears and vanishes.
 */
export function usePermissions({ poll = false }: { poll?: boolean } = {}) {
  const { isAuthenticated } = useAuth();
  const query = useQuery({
    queryKey: adminAccessKeys.me(),
    queryFn: ({ signal }) => getMyPermissions(signal),
    enabled: isAuthenticated,
    staleTime: 60_000,
    refetchInterval: poll ? 60_000 : false,
    refetchOnWindowFocus: true,
  });

  const held = useMemo(
    () => new Set<string>(query.data?.permissions ?? []),
    [query.data],
  );
  const can = useCallback((permission: PermissionKey) => held.has(permission), [held]);

  return {
    can,
    isOwner: query.data?.isOwner ?? false,
    /** True only before the first answer — a background refetch keeps the old one. */
    isLoading: query.isPending,
    error: query.isError && !query.data ? query.error : null,
    refetch: query.refetch,
  };
}
